import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { TWCButton } from '@components';
import axios from 'axios';
import { useRecoilState } from 'recoil';

import { assets } from '@assets';
import { apiConfig, onboardingTestIDs, Theme } from '@constants';
import { useContextReference, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { onboardingFlowState, userEmailState } from '@store';

import { RadialGradientBackground } from '../../components/loading/RadialGradientBackground';
import { TWCPage } from '../../components/primitives/TWCPage/TWCPage';
import { TWCText } from '../../components/primitives/TWCText/TWCText';
import { TWCTextInput } from '../../components/primitives/TWCTextInput/TWCTextInput';
import { isAndroid } from '../../helpers/Platform';

const tPath = 'subscriberSignUpPage.';

export const SubscriberSignUpPage = () => (
  <TWCPage
    childIds={[
      onboardingTestIDs.subscriberSignUp.continueButton,
      onboardingTestIDs.subscriberSignUp.emailInput,
      onboardingTestIDs.subscriberSignUp.headingText,
      onboardingTestIDs.subscriberSignUp.logoImage,
    ]}
    firstFocus={onboardingTestIDs.subscriberSignUp.emailInput}
    pageID={onboardingTestIDs.pages.subscriberSignUpPage}
  >
    <RadialGradientBackground>
      <SubscriberSignUpPageContent />
    </RadialGradientBackground>
  </TWCPage>
);

const SubscriberSignUpPageContent = () => {
  const emailInputReference = useContextReference(onboardingTestIDs.subscriberSignUp.emailInput);

  const continueButtonReference = useContextReference(onboardingTestIDs.subscriberSignUp.continueButton);

  const emailInputHandle = useNodeHandle(emailInputReference);
  const continueButtonHandle = useNodeHandle(continueButtonReference);
  const [onboardingFlow, setOnboardingFlow] = useRecoilState(onboardingFlowState);

  const emailNoFocus = isAndroid ? emailInputHandle : null;
  const continueButtonNoFocus = isAndroid ? continueButtonHandle : null;

  const getPaidCode = async () => {
    try {
      const getPaidCodeBody = {
        client_id: apiConfig.AUTH0_CLIENT_ID,
        connection: 'email',
        email: userEmail,
        send: 'code',
        authParams: {
          scope: 'openid profile',
        },
      };

      await axios.post(apiConfig.AUTH0_START_URL, getPaidCodeBody);
      setOnboardingFlow({ ...onboardingFlow, signUp: true });
      navigationService.navigate(onboardingTestIDs.pages.authVerificationPage);
    } catch (error) {
      console.warn('GET auth0 passwordless/start error:', error);
      navigationService.navigate(onboardingTestIDs.pages.errorPage, { error });
    }
  };

  const { t } = useTranslation();
  /*
    This email will eventually get pulled in automatically after spike story in WEXF-314 is complete
  */
  const [userEmail, setUserEmail] = useRecoilState(userEmailState);

  return (
    <View style={Theme.styles.center}>
      <Image source={assets.twcLogo} style={styles.logoImage} testID={onboardingTestIDs.subscriberSignUp.logoImage} />
      <TWCText
        style={styles.headingText}
        testID={onboardingTestIDs.subscriberSignUp.headingText}
        variant={'regularText36Transparent'}
      >
        {t(`${tPath}subscriberSignUpHeading.headingText`)}
      </TWCText>
      <TWCTextInput
        accessibilityHint={t(`${tPath}subscriberSignUpInstructions.accessibilityHint`)}
        accessibilityLabel={t(`${tPath}subscriberSignUpInstructions.accessibilityLabel`)}
        borderColorDefault={styles.emailEntryInputBorder}
        hideKeyboardInput
        keyboardDismissConfig={{ right: true }}
        keyboardWrapConfig={{ horizontal: false }}
        nextFocusDown={continueButtonHandle}
        nextFocusLeft={emailNoFocus}
        nextFocusRight={emailNoFocus}
        nextFocusUp={emailNoFocus}
        placeholder={t(`${tPath}subscriberSignUpInstructions.emailInputPlaceholderText`)}
        placeholderTextColor={Theme.withOpacity(Theme.white, 0.5)}
        ref={emailInputReference}
        returnKeyType={'done'}
        style={styles.emailEntryInput}
        styleFocused={styles.emailEntryInputFocused}
        testID={onboardingTestIDs.subscriberSignUp.emailInput}
        value={userEmail ?? ''}
        onChangeText={text => {
          setUserEmail(text);
        }}
      />
      <TWCButton
        nextFocusDown={continueButtonNoFocus}
        nextFocusLeft={continueButtonNoFocus}
        nextFocusRight={continueButtonNoFocus}
        nextFocusUp={emailInputHandle}
        ref={continueButtonReference}
        style={styles.continueButton}
        testID={onboardingTestIDs.subscriberSignUp.continueButton}
        title={t(`${tPath}subscriberSignUpInstructions.continueButtonText`)}
        onPress={() => getPaidCode()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  continueButton: {
    height: Theme.px(90),
    width: Theme.px(422),
  },
  emailEntryInput: {
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.1),
    color: Theme.white,
    marginBottom: Theme.px(239),
    opacity: 1,
    textAlign: 'left',
    width: Theme.px(799),
  },
  emailEntryInputBorder: {
    borderColor: Theme.withOpacity(Theme.whisperWhite, 0.4),
  },
  emailEntryInputFocused: {
    borderColor: Theme.dodgerBlue,
    color: Theme.white,
  },
  headingText: {
    color: Theme.white,
    lineHeight: Theme.px(55),
    marginBottom: Theme.px(17),
  },
  logoImage: {
    height: Theme.px(156),
    marginBottom: Theme.px(192),
    width: Theme.px(156),
  },
});
